import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Col,
    Badge,
    Row,
    Form,
    Tooltip,
    OverlayTrigger,
    InputGroup,
    ButtonToolbar,
} from "react-bootstrap";
import {
    faExclamationCircle,
    faClock,
    faClose,
    faCheck,
    faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HelpTooltip } from "../../assets/functions.js";

export const formColumns = [
    { description: '6/6', value: 12 },
    { description: '4/6', value: 8 },
    { description: '3/6', value: 6 },
    { description: '2/6', value: 4 },
    { description: '1/6', value: 2 },
];

const getCorrectAnswerCount = (answers = []) => (
    Array.isArray(answers)
        ? answers.filter((answer) => answer?.isCorrect === true).length
        : 0
);

const getAnswerCount = (answers = []) => (
    Array.isArray(answers) ? answers.length : 0
);

const getNextQuestionId = (questions = []) => {
    const questionIds = questions
        .map((question) => Number(question?.question_id))
        .filter((questionId) => Number.isInteger(questionId) && questionId > 0);

    return (questionIds.length > 0 ? Math.max(...questionIds) : 0) + 1;
};

const clampMultipleChoiceLimit = (value, min, max) => {
    const normalizedValue = Number(value);
    const normalizedMin = Number(min);
    const normalizedMax = Number(max);

    if (!Number.isInteger(normalizedValue)) {
        return normalizedMin;
    }

    return Math.min(Math.max(normalizedValue, normalizedMin), normalizedMax);
};

const getMultipleChoiceDefaults = (question, exercise = question?.exercise, answers = question?.answers) => {
    const answerCount = Math.max(getAnswerCount(answers), 1);

    if (Number(exercise) === 1) {
        const correctCount = Math.max(getCorrectAnswerCount(answers), 1);
        return { min: correctCount, max: correctCount };
    }

    return { min: 1, max: Math.min(2, answerCount) };
};

const ActivityQuizForm = ({
    handleChange,
    values,
    setFieldValue,
    errors,
    touched,
    submitCount,
    availabilityFields,
}) => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const questionToolbarRef = useRef(null);

    useEffect(() => {
        if (!submitCount || !Array.isArray(errors?.json?.questions)) {
            return;
        }

        const firstQuestionErrorIndex = errors.json.questions.findIndex(Boolean);
        if (firstQuestionErrorIndex < 0) {
            return;
        }

        setCurrentQuestion(firstQuestionErrorIndex);

        window.setTimeout(() => {
            const toolbar = questionToolbarRef.current;
            const invalidButton = toolbar?.querySelector('.buttonToolbarApp.is-invalid');
            const scrollTarget = invalidButton || toolbar;

            if (scrollTarget) {
                scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 0);
    }, [errors?.json?.questions, submitCount]);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (index) => {
        if (draggedIndex === index) return;
        const updatedQuestions = [...values.json.questions];
        const [draggedItem] = updatedQuestions.splice(draggedIndex, 1);
        updatedQuestions.splice(index, 0, draggedItem);
        setDraggedIndex(index);
        values.json.questions = updatedQuestions;
    };

    const handleDragEnd = () => {
        setFieldValue("json.questions", [...values.json.questions]);
        setDraggedIndex(null);
    };

    const exerciseType = (exercise) => {
        switch (exercise) {
            case 1:
                return "Múltipla escolha";
            case 2:
                return "Enquete";
            case 3:
                return "Resposta em texto";
            default:
                return "";
        }
    };

    const createDefaultAnswers = () => ([
        { text: "", isCorrect: false, order: 0, showInput: false },
        { text: "", isCorrect: false, order: 1, showInput: false },
        { text: "", isCorrect: false, order: 2, showInput: false },
        { text: "", isCorrect: false, order: 3, showInput: false },
    ]);

    const syncMultipleChoiceLimits = (index, options = {}) => {
        const question = values.json.questions[index] || {};
        const exercise = options.exercise ?? question.exercise;
        const answers = options.answers ?? question.answers;
        const enabled = options.enabled ?? question.enableMultipleChoice === true;
        const answerCount = Math.max(getAnswerCount(answers), 1);

        if (!enabled) {
            setFieldValue(`json.questions[${index}].multipleChoiceMin`, 1);
            setFieldValue(`json.questions[${index}].multipleChoiceMax`, 1);
            return;
        }

        const defaultLimits = getMultipleChoiceDefaults(question, exercise, answers);
        const nextMin = clampMultipleChoiceLimit(question.multipleChoiceMin ?? defaultLimits.min, 1, answerCount);
        const nextMax = clampMultipleChoiceLimit(question.multipleChoiceMax ?? defaultLimits.max, nextMin, answerCount);
        setFieldValue(`json.questions[${index}].multipleChoiceMin`, nextMin);
        setFieldValue(`json.questions[${index}].multipleChoiceMax`, nextMax);
    };

    const handleExerciseTypeChange = (index, nextExercise) => {
        setFieldValue(`json.questions[${index}].exercise`, nextExercise);

        if (nextExercise === 3) {
            setFieldValue(`json.questions[${index}].answers`, []);
            setFieldValue(`json.questions[${index}].enableMultipleChoice`, false);
            setFieldValue(`json.questions[${index}].multipleChoiceMin`, 1);
            setFieldValue(`json.questions[${index}].multipleChoiceMax`, 1);
            return;
        }

        if (!Array.isArray(values.json.questions[index]?.answers) || values.json.questions[index].answers.length === 0) {
            const defaultAnswers = createDefaultAnswers();
            const normalizedAnswers = nextExercise === 1
                ? defaultAnswers.map((answer, answerIndex) => ({
                    ...answer,
                    isCorrect: answerIndex === 0,
                }))
                : defaultAnswers;

            setFieldValue(`json.questions[${index}].answers`, normalizedAnswers);
            syncMultipleChoiceLimits(index, { exercise: nextExercise, answers: normalizedAnswers });
            return;
        }

        syncMultipleChoiceLimits(index, { exercise: nextExercise });
    };

    function newQuestion() {
        // Legacy activities may not have persisted a questions array. Treat them
        // as an empty quiz so adding a question always repairs the form instead
        // of throwing before Formik can update it.
        const questions = Array.isArray(values?.json?.questions) ? values.json.questions : [];
        const newID = questions.length;
        const questionId = getNextQuestionId(questions);
        setFieldValue("json.questions", [
            ...questions,
            {
                question_id: questionId,
                exercise: 1,
                type: 1,
                col: 6,
                question: "",
                url: "",
                shuffleAnswers: true,
                value: 0,
                enableMultipleChoice: false,
                multipleChoiceMin: 1,
                multipleChoiceMax: 1,
                additionalAnswer: {
                    status: false,
                    text: "",
                    image: "",
                },
                answers: createDefaultAnswers().map((answer, answerIndex) => ({
                    ...answer,
                    isCorrect: answerIndex === 0,
                })),
            },
        ]);
        setCurrentQuestion(newID);
    }

    function deleteQuestion(index) {
        const questions = Array.isArray(values?.json?.questions) ? values.json.questions : [];
        const newQuestions = questions.filter((_, i) => i !== index);
        setFieldValue("json.questions", newQuestions);
        setCurrentQuestion(index > 0 ? index - 1 : 0);
    }

    return (
        <>
            <Form.Group as={Col} className="textHeaderAplicativo mb-3">
                <h5 className="card-title mb-0">Definições</h5>
            </Form.Group>
            <Row className="mx-0 mb-3">
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        type="text"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                        isInvalid={!!errors.title}
                        isValid={touched.title && !errors.title}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.title}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm="12">
                    <Form.Check
                        checked={values.json.timer?.status === true ? true : false}
                        type="checkbox"
                        name="json.timer.status"
                        label="Limite de tempo"
                        onChange={(e) => {
                            setFieldValue(
                                "json.timer.status",
                                values.json.timer?.status === true ? false : true
                            );
                        }}
                    />
                    {values.json.timer?.status === true ? (
                        <Row>
                            <Form.Group className="mb-3" as={Col} sm="6">
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faClock} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        name="json.timer.value"
                                        value={values.json.timer.value}
                                        onChange={handleChange}
                                        isInvalid={!!errors.json?.timer?.value}
                                        isValid={touched.json?.timer?.value && !errors.json?.timer?.value}
                                    />
                                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.json?.timer?.value}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className="mb-3" as={Col} sm="6">
                                <Form.Select
                                    name="json.timer.position"
                                    value={values.json.timer?.position || "belowQuestion"}
                                    onChange={handleChange}
                                    isInvalid={!!errors.json?.timer?.position}
                                    isValid={touched.json?.timer?.position && !errors.json?.timer?.position}
                                >
                                    <option value="belowQuestion">Abaixo da pergunta</option>
                                    <option value="aboveQuestion">Acima da pergunta</option>
                                </Form.Select>
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.timer?.position}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                    ) : ""}
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12">
                    {availabilityFields}
                </Form.Group>
            </Row>
            <Form.Group as={Col} className="textHeaderAplicativo mb-2">
                <h5 className="card-title mb-0">Exercícios</h5>
            </Form.Group>
            <Row className="mx-0 mt-3 mb-0">
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Check
                        checked={values.json.shuffleQuestions === true ? true : false}
                        type="checkbox"
                        name="json.shuffleQuestions"
                        label={
                            <>
                                Embaralhar ordenação dos exercícios
                                <OverlayTrigger
                                    overlay={<Tooltip>Adicione o número das questões que você deseja manter fixo na ordenação, separado por vírgulas. Ex: 1,3,5</Tooltip>}
                                >
                                    <HelpTooltip icon={faExclamationCircle} />
                                </OverlayTrigger>
                            </>
                        }
                        onChange={(e) => {
                            setFieldValue(
                                "json.shuffleQuestions",
                                values.json.shuffleQuestions === true ? false : true
                            );
                        }}
                    />
                    {values.json.shuffleQuestions === true ? (
                        <Form.Group className="mb-3" as={Col} sm="6">
                            <Form.Control
                                type="text"
                                name="json.shuffleQuestionsArray"
                                value={values.json.shuffleQuestionsArray}
                                onChange={handleChange}
                                isInvalid={!!errors.json?.shuffleQuestionsArray}
                                isValid={touched.json?.shuffleQuestionsArray && !errors.json?.shuffleQuestionsArray}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.shuffleQuestionsArray}
                            </Form.Control.Feedback>
                        </Form.Group>
                    ) : ""}
                    <Form.Check
                        checked={values.json.answerFeedback === true ? true : false}
                        type="checkbox"
                        name="json.answerFeedback"
                        label="Feedback de acerto ou erro"
                        onChange={(e) => {
                            setFieldValue(
                                "json.answerFeedback",
                                values.json.answerFeedback === true ? false : true
                            );
                        }}
                    />
                    <Form.Check
                        checked={values.json.correctFeedback === true ? true : false}
                        type="checkbox"
                        name="json.correctFeedback"
                        label="Destacar resposta correta"
                        onChange={(e) => {
                            setFieldValue(
                                "json.correctFeedback",
                                values.json.correctFeedback === true ? false : true
                            );
                        }}
                    />
                    <Form.Check
                        checked={values.json.selectQuestions?.status === true ? true : false}
                        type="checkbox"
                        name="json.selectQuestions.status"
                        label={
                            <>
                                Limite de exercícios
                                <OverlayTrigger
                                    overlay={<Tooltip>Ao definir um valor menor que o total de exercícios, é feita uma seleção aleatória de exercícios antes de iniciar o Quiz</Tooltip>}
                                >
                                    <HelpTooltip icon={faExclamationCircle} />
                                </OverlayTrigger>
                            </>
                        }
                        onChange={(e) => {
                            setFieldValue(
                                "json.selectQuestions.status",
                                values.json.selectQuestions?.status === true ? false : true
                            );
                        }}
                    />
                    {values.json.selectQuestions?.status === true ? (
                        <Form.Group className="mb-3" as={Col} sm="6">
                            <Form.Control
                                type="number"
                                name="json.selectQuestions.value"
                                value={values.json.selectQuestions?.value}
                                onChange={handleChange}
                                isInvalid={!!errors.json?.selectQuestions?.value}
                                isValid={touched.json?.selectQuestions?.value && !errors.json?.selectQuestions?.value}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.selectQuestions?.value}
                            </Form.Control.Feedback>
                        </Form.Group>
                    ) : ""}
                </Form.Group>
            </Row>
            <hr />
            <ButtonToolbar ref={questionToolbarRef} className="mb-2 justify-content-center">
                {values.json.questions?.map((element, index) => {
                    let hasError = false;
                    if (errors?.json?.questions?.[index]) {
                        hasError = true;
                    }
                    return (
                        <Button
                            key={"buttonQuestions" + index}
                            className={`mt-3 me-2 buttonToolbarApp ${hasError ? 'is-invalid' : ''} ${currentQuestion === index ? 'active' : ''}`}
                            onClick={() => setCurrentQuestion(index)}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => {
                                e.preventDefault();
                                handleDragOver(index);
                            }}
                            onDragEnd={handleDragEnd}
                        >
                            {index + 1}
                        </Button>
                    );
                })}
                <Button onClick={() => newQuestion()} className="mt-3 ms-2">
                    Adicionar
                </Button>
            </ButtonToolbar>
            <Col className="text-center mb-3">
                <small className="text-center opacity-75">(Clique e arraste para ordenar)</small>
            </Col>
            {values.json.questions?.map((element, index) => {
                if (index !== currentQuestion) {
                    return "";
                }
                return (
                    <Row className="mx-0 mb-3" key={'questionsCard' + index}>
                        <Form.Group as={Col} sm="6" className="mb-3">
                            <Form.Label>Tipo de exercício</Form.Label>
                            <Form.Select
                                name={`json.questions[${index}].exercise`}
                                value={values.json.questions[index]?.exercise}
                                onChange={(e) => {
                                    handleExerciseTypeChange(index, parseInt(e.target.value));
                                }}
                            >
                                <option value={1}>{exerciseType(1)}</option>
                                <option value={2}>{exerciseType(2)}</option>
                                <option value={3}>{exerciseType(3)}</option>
                            </Form.Select>
                        </Form.Group>
                        {Number(values.json.questions[index]?.exercise) === 1 && (
                            <Form.Group className="mb-3" as={Col} sm="6">
                                <Form.Label>Pontos por acerto</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="1"
                                    name={`json.questions[${index}].value`}
                                    value={values.json.questions[index]?.value ?? 0}
                                    onChange={handleChange}
                                    isInvalid={!!errors.json?.questions?.[index]?.value}
                                    isValid={touched.json?.questions?.[index]?.value && !errors.json?.questions?.[index]?.value}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.questions?.[index]?.value}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3" as={Col} sm="6">
                            <Form.Label>
                                Tamanho das respostas{" "}
                                <OverlayTrigger
                                    overlay={
                                        <Tooltip>
                                            6/6 = 100% de largura<br />
                                            4/6 = 80% de largura<br />
                                            3/6 = 60% de largura<br />
                                            2/6 = 40% de largura<br />
                                            1/6 = 20% de largura
                                        </Tooltip>
                                    }
                                >
                                    <HelpTooltip icon={faExclamationCircle} />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                                name="col"
                                value={values.json.questions[index].col}
                                onChange={(e) => {
                                    setFieldValue(`json.questions[${index}].col`, parseInt(e.target.value));
                                }}
                                isInvalid={!!errors.json?.questions?.[index]?.col}
                                isValid={touched.json?.questions?.[index]?.col && !errors.json?.questions?.[index]?.col}
                            >
                                {formColumns.map((col, colIndex) => (
                                    <option key={"colField" + colIndex} value={col.value}>
                                        {col.description}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.questions?.[index]?.col}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" as={Col} sm="12">
                            <Form.Label>Questão</Form.Label>
                            <Form.Control
                                type="text"
                                as="textarea"
                                name={`json.questions[${index}].question`}
                                value={values.json.questions[index].question}
                                onChange={handleChange}
                                isInvalid={!!errors?.json?.questions?.[index]?.question}
                                isValid={touched.json?.questions?.[index]?.question && !errors.json?.questions?.[index]?.question}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.questions?.[index]?.question}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <ExerciseType
                            values={values}
                            currentQuestion={index}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            handleChange={handleChange}
                        />
                        {values.json.questions?.length > 1 && (
                            <Col className="text-center">
                                <Button
                                    variant="danger"
                                    className="mb-3"
                                    onClick={() => deleteQuestion(currentQuestion)}
                                >
                                    Excluir
                                </Button>
                            </Col>
                        )}
                    </Row>
                );
            })}
            <Form.Group as={Col} className="textHeaderAplicativo mb-3">
                <h5 className="card-title mb-0">Participante</h5>
            </Form.Group>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Tela de resultado</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.endText.title.value"
                        value={values.json.endText.title.value}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setFieldValue("json.endText.title.status", false);
                            } else {
                                setFieldValue("json.endText.title.status", true);
                            }
                            setFieldValue("json.endText.title.value", e.target.value);
                        }}
                        isInvalid={!!errors?.json?.endText?.title?.value}
                        isValid={touched?.json?.endText?.title?.value && !errors.json?.endText?.title?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.endText?.title?.value}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.endText.description.value"
                        value={values.json.endText.description.value}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setFieldValue("json.endText.description.status", false);
                            } else {
                                setFieldValue("json.endText.description.status", true);
                            }
                            setFieldValue("json.endText.description.value", e.target.value);
                        }}
                        isInvalid={!!errors?.json?.endText?.description?.value}
                        isValid={touched?.json?.endText?.description?.value && !errors.json?.endText?.description?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.endText?.description?.value}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Check
                        checked={values.json.showScore ? true : false}
                        type="checkbox"
                        name="json.showScore"
                        label="Mostrar pontuação ao participante"
                        onChange={(e) => {
                            setFieldValue(
                                "json.showScore",
                                values.json.showScore ? false : true
                            );
                        }}
                    />
                    <Form.Check
                        checked={values.json.allowPreviousQuestion === true}
                        type="checkbox"
                        name="json.allowPreviousQuestion"
                        label="Permitir voltar para a pergunta anterior"
                        onChange={() => {
                            setFieldValue(
                                "json.allowPreviousQuestion",
                                values.json.allowPreviousQuestion === true ? false : true
                            );
                        }}
                    />
                    <Form.Check
                        checked={values.json.retry?.status === true ? true : false}
                        type="checkbox"
                        name="json.retry.status"
                        label="Botão de jogar novamente"
                        onChange={(e) => {
                            setFieldValue(
                                "json.retry.status",
                                values.json.retry?.status === true ? false : true
                            );
                        }}
                    />
                    {values.json.retry?.status === true ? (
                        <Form.Group className="mb-3" as={Col} sm="6">
                            <Form.Label>Texto do botão</Form.Label>
                            <Form.Control
                                name="json.retry.text"
                                value={values.json.retry?.text || ""}
                                onChange={handleChange}
                                isInvalid={!!errors.json?.retry?.text}
                                isValid={touched.json?.retry?.text && !errors.json?.retry?.text}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.retry?.text}
                            </Form.Control.Feedback>
                        </Form.Group>
                    ) : ""}
                </Form.Group>
            </Row>
        </>
    );
};

const ExerciseType = ({
    values,
    currentQuestion,
    errors,
    touched,
    setFieldValue,
    handleChange,
}) => {
    const question = values.json.questions[currentQuestion] || {};
    const answers = Array.isArray(question.answers) ? question.answers : [];
    const currentExercise = Number(question.exercise);
    const multipleChoiceEnabled = question.enableMultipleChoice === true;

    const syncMultipleChoiceLimitsForAnswers = (nextAnswers) => {
        if (!multipleChoiceEnabled) {
            return;
        }

        const answerCount = Math.max(getAnswerCount(nextAnswers), 1);
        const defaultLimits = getMultipleChoiceDefaults(question, currentExercise, nextAnswers);
        const nextMin = clampMultipleChoiceLimit(question.multipleChoiceMin ?? defaultLimits.min, 1, answerCount);
        const nextMax = clampMultipleChoiceLimit(question.multipleChoiceMax ?? defaultLimits.max, nextMin, answerCount);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMin`, nextMin);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMax`, nextMax);
    };

    const handleMultipleChoiceToggle = () => {
        const nextEnabled = !multipleChoiceEnabled;
        setFieldValue(`json.questions[${currentQuestion}].enableMultipleChoice`, nextEnabled);

        if (!nextEnabled) {
            setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMin`, 1);
            setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMax`, 1);
            return;
        }

        const { min, max } = getMultipleChoiceDefaults(question, currentExercise, answers);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMin`, min);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMax`, max);
    };

    const handleMultipleChoiceLimitChange = (field, value) => {
        const answerCount = Math.max(getAnswerCount(answers), 1);
        const defaultLimits = getMultipleChoiceDefaults(question, currentExercise, answers);

        if (field === 'min') {
            const nextMin = clampMultipleChoiceLimit(value, 1, answerCount);
            const nextMax = clampMultipleChoiceLimit(question.multipleChoiceMax ?? defaultLimits.max, nextMin, answerCount);
            setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMin`, nextMin);
            setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMax`, nextMax);
            return;
        }

        const currentMin = clampMultipleChoiceLimit(question.multipleChoiceMin ?? defaultLimits.min, 1, answerCount);
        const nextMax = clampMultipleChoiceLimit(value, currentMin, answerCount);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMin`, currentMin);
        setFieldValue(`json.questions[${currentQuestion}].multipleChoiceMax`, nextMax);
    };

    const renderMultipleChoiceSettings = () => {
        if (![1, 2].includes(currentExercise)) {
            return null;
        }

        const defaultLimits = getMultipleChoiceDefaults(question, currentExercise, answers);
        const minValue = question.multipleChoiceMin ?? defaultLimits.min;
        const maxValue = question.multipleChoiceMax ?? defaultLimits.max;

        return (
            <Form.Group as={Col} sm="12" className="mb-3">
                <Form.Check
                    checked={multipleChoiceEnabled}
                    type="checkbox"
                    name={`json.questions[${currentQuestion}].enableMultipleChoice`}
                    label="Habilitar múltiplas respostas"
                    onChange={handleMultipleChoiceToggle}
                />
                {multipleChoiceEnabled && (
                    <Row className="mt-2">
                        <Form.Group as={Col} sm="6" className="mb-2">
                            <Form.Label>Mínimo de respostas</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                max={Math.max(getAnswerCount(answers), 1)}
                                value={minValue}
                                onChange={(e) => handleMultipleChoiceLimitChange('min', e.target.value)}
                                isInvalid={!!errors.json?.questions?.[currentQuestion]?.multipleChoiceMin}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.questions?.[currentQuestion]?.multipleChoiceMin}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} sm="6" className="mb-2">
                            <Form.Label>Máximo de respostas</Form.Label>
                            <Form.Control
                                type="number"
                                min={minValue}
                                max={Math.max(getAnswerCount(answers), 1)}
                                value={maxValue}
                                onChange={(e) => handleMultipleChoiceLimitChange('max', e.target.value)}
                                isInvalid={!!errors.json?.questions?.[currentQuestion]?.multipleChoiceMax}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.questions?.[currentQuestion]?.multipleChoiceMax}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                )}
            </Form.Group>
        );
    };

    function removeAnswer(order) {
        const newAnswers = values.json.questions[currentQuestion].answers
            .filter((obj) => obj.order !== order);
        const hasCorrectAnswer = newAnswers.some((a) => a.isCorrect === true);
        if (!hasCorrectAnswer && newAnswers.length > 0) {
            newAnswers[0] = { ...newAnswers[0], isCorrect: true };
        }
        setFieldValue(`json.questions[${currentQuestion}].answers`, newAnswers);
        syncMultipleChoiceLimitsForAnswers(newAnswers);
    }

    function addAnswer() {
        const newAnswer = {
            text: "",
            isCorrect: false,
            order: values.json.questions[currentQuestion].answers.length,
            showInput: false,
        };
        setFieldValue(
            `json.questions[${currentQuestion}].answers`,
            [...values.json.questions[currentQuestion].answers, newAnswer]
        );
        syncMultipleChoiceLimitsForAnswers([...values.json.questions[currentQuestion].answers, newAnswer]);
    }

    function toggleCorrectAnswer(index) {
        const currentAnswers = values.json.questions[currentQuestion].answers || [];
        const isTurningCorrectOn = currentAnswers[index]?.isCorrect !== true;
        const maxCorrectAnswers = Number(question.multipleChoiceMax);
        const shouldLimitCorrectAnswers = multipleChoiceEnabled
            && currentExercise === 1
            && Number.isInteger(maxCorrectAnswers)
            && maxCorrectAnswers > 0
            && isTurningCorrectOn
            && getCorrectAnswerCount(currentAnswers) >= maxCorrectAnswers;

        if (shouldLimitCorrectAnswers) {
            return;
        }

        const nextAnswers = currentAnswers.map((answer, answerIndex) => (
            answerIndex === index
                ? { ...answer, isCorrect: !answer.isCorrect }
                : answer
        ));
        const hasCorrectAnswer = nextAnswers.some((answer) => answer.isCorrect === true);

        if (!hasCorrectAnswer && nextAnswers[index]) {
            nextAnswers[index] = { ...nextAnswers[index], isCorrect: true };
        }

        setFieldValue(`json.questions[${currentQuestion}].answers`, nextAnswers);
        syncMultipleChoiceLimitsForAnswers(nextAnswers);
    }

    switch (values.json.questions[currentQuestion].exercise) {
        case 1:
            return (
                <>
                    <div className="mb-3">
                        <Form.Group as={Col} sm="12">
                            <Form.Check
                                checked={values.json.questions[currentQuestion].shuffleAnswers === true ? true : false}
                                type="checkbox"
                                name={`values.json.questions[${currentQuestion}].shuffleAnswers`}
                                label="Embaralhar ordenação das respostas"
                                onChange={(e) => {
                                    setFieldValue(
                                        `json.questions[${currentQuestion}].shuffleAnswers`,
                                        values.json.questions[currentQuestion].shuffleAnswers === true ? false : true
                                    );
                                }}
                            />
                        </Form.Group>
                        {renderMultipleChoiceSettings()}
                    </div>
                    <Form.Group as={Col} sm="12">
                        <Form.Label>
                            Respostas
                            <Badge pill className="ms-2 bg-success">
                                {values.json.questions[currentQuestion].answers.filter((answer) => answer.isCorrect === true).length} correta(s)
                            </Badge>
                            {values.json.questions[currentQuestion].answers.length < 10 && (
                                <Badge
                                    pill
                                    className={"ms-2 bg-primary text-dark cursor-pointer"}
                                    onClick={() => addAnswer()}
                                >
                                    Adicionar
                                </Badge>
                            )}
                        </Form.Label>
                        {errors.json?.questions?.[currentQuestion]?.correctAnswer && (
                            <small className="text-danger d-block mb-2">
                                {errors.json.questions[currentQuestion].correctAnswer}
                            </small>
                        )}
                        {values.json.questions[currentQuestion].answers.map((element, index) => (
                            <Form.Group key={"questions" + index} className="mb-3" as={Col} sm="12">
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name={`json.questions[${currentQuestion}].answers[${index}].text`}
                                        value={values.json.questions[currentQuestion].answers[index].text}
                                        onChange={handleChange}
                                        isInvalid={touched.json?.questions?.[currentQuestion]?.answers?.[index]?.text && !!errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                        isValid={touched.json?.questions?.[currentQuestion]?.answers?.[index]?.text && !errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                    />
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip>
                                                Marcar ou desmarcar como correta
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            type="button"
                                            variant={values.json.questions[currentQuestion].answers[index].isCorrect === true ? 'success' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => toggleCorrectAnswer(index)}
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                        </Button>
                                    </OverlayTrigger>
                                    {/* `showInput` is only available for Enquete (exercise type 2). */}
                                    {values.json.questions[currentQuestion].answers.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="danger"
                                            onClick={() => removeAnswer(element.order)}
                                        >
                                            <FontAwesomeIcon icon={faClose} />
                                        </Button>
                                    )}
                                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        ))}
                    </Form.Group>
                </>
            );
        case 2:
            return (
                <>
                    <div className="mb-3">
                        <Form.Group as={Col} sm="12">
                            <Form.Check
                                checked={values.json.questions[currentQuestion].shuffleAnswers === true ? true : false}
                                type="checkbox"
                                name={`values.json.questions[${currentQuestion}].shuffleAnswers`}
                                label="Embaralhar ordenação das respostas"
                                onChange={(e) => {
                                    setFieldValue(
                                        `json.questions[${currentQuestion}].shuffleAnswers`,
                                        values.json.questions[currentQuestion].shuffleAnswers === true ? false : true
                                    );
                                }}
                            />
                        </Form.Group>
                        {renderMultipleChoiceSettings()}
                    </div>
                    <Form.Group as={Col} sm="12">
                        <Form.Label>
                            Respostas
                            {values.json.questions[currentQuestion].answers.length < 10 && (
                                <Badge
                                    pill
                                    className={"ms-2 bg-primary cursor-pointer"}
                                    onClick={() => addAnswer()}
                                >
                                    Adicionar
                                </Badge>
                            )}
                        </Form.Label>
                        {values.json.questions[currentQuestion].answers.map((element, index) => (
                            <Form.Group key={"questions" + index} className="mb-3" as={Col} sm="12">
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name={`json.questions[${currentQuestion}].answers[${index}].text`}
                                        value={values.json.questions[currentQuestion].answers[index].text}
                                        onChange={handleChange}
                                        isInvalid={!!errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                        isValid={touched.json?.questions?.[currentQuestion]?.answers?.[index]?.text && !errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                    />
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip>
                                                Ao selecionar esta alternativa, o participante poderá
                                                escrever um comentário opcional antes de continuar.
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            type="button"
                                            variant={values.json.questions[currentQuestion].answers[index].showInput === true ? 'success' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => {
                                                setFieldValue(`json.questions[${currentQuestion}].answers[${index}].showInput`, !values.json.questions[currentQuestion].answers[index].showInput);
                                            }}
                                            title="Mostrar campo de texto ao selecionar"
                                        >
                                            <FontAwesomeIcon icon={faCommentDots} />
                                        </Button>
                                    </OverlayTrigger>
                                    {values.json.questions[currentQuestion].answers.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="danger"
                                            onClick={() => removeAnswer(element.order)}
                                        >
                                            <FontAwesomeIcon icon={faClose} />
                                        </Button>
                                    )}
                                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.json?.questions?.[currentQuestion]?.answers?.[index]?.text}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        ))}
                    </Form.Group>
                </>
            );
        case 3:
            return (
                <Form.Group as={Col} sm="12" className="mb-3">
                    <Form.Label>Configuração da resposta</Form.Label>
                    <div className="border rounded p-3 bg-light">
                        Este tipo exibe um campo de texto no aplicativo.
                        Não há configuração de alternativas para esta questão.
                    </div>
                </Form.Group>
            );
        default:
            return "";
    }
};

export default ActivityQuizForm;
